/**
 * The face a set wears while its lane is still answering.
 *
 * It is skeleton geometry in the shape of the layout that is coming — rows for
 * Rows, a deck for Rail, cards for Cards, one deck card for Inline, one tall
 * pane for Detail — so the surface does not jump when the real content lands.
 * That is the whole reason it is not a spinner: a spinner says "something is
 * happening somewhere", while a skeleton says "this many of these, here".
 *
 * It is visually distinct from both of its neighbours: it pulses, it has no
 * artwork and no reason line, and it announces itself as busy rather than as
 * an answer.
 */
"use client";
import type { CSSProperties, ReactElement } from "react";
import { Ccontainerskeleton } from "@/components/ui/Ccontainerskeleton";
import {
  CRC_R_CORE,
  CRC_RAIL_CARD_WIDTH,
  CRC_RAIL_GAP,
  CRC_RAIL_INSET,
} from "./cresultcard.tokens";
import { CresultcardLayout } from "./cresultcard.types";

/** Enough to read as a set. More is a longer wait pretending to be progress. */
const PLACEHOLDER_COUNT = 3;
const SINGLE_PLACEHOLDER = 1;
const ROW_HEIGHT = 46;
const CARD_HEIGHT = 168;
const RAIL_CARD_HEIGHT = 196;
const DETAIL_HEIGHT = 320;
const STACK_GAP = 8;
const NO_INSET = 0;
const FULL_WIDTH = "100%";
const LOADING_LABEL = "Loading results";

enum SkeletonFlow {
  Row = "row",
  Column = "column",
}

interface SkeletonGeometry {
  readonly count: number;
  readonly height: number;
  readonly flow: SkeletonFlow;
  readonly width: CSSProperties["width"];
  readonly inset: number;
}

/** What is coming, per layout: how many, how tall, stacked or decked. */
const GEOMETRY_BY_LAYOUT: Record<CresultcardLayout, SkeletonGeometry> = {
  [CresultcardLayout.Rows]: {
    count: PLACEHOLDER_COUNT,
    height: ROW_HEIGHT,
    flow: SkeletonFlow.Column,
    width: FULL_WIDTH,
    inset: CRC_RAIL_INSET,
  },
  [CresultcardLayout.Rail]: {
    count: PLACEHOLDER_COUNT,
    height: RAIL_CARD_HEIGHT,
    flow: SkeletonFlow.Row,
    width: CRC_RAIL_CARD_WIDTH,
    inset: CRC_RAIL_INSET,
  },
  [CresultcardLayout.Cards]: {
    count: PLACEHOLDER_COUNT,
    height: CARD_HEIGHT,
    flow: SkeletonFlow.Column,
    width: FULL_WIDTH,
    inset: CRC_RAIL_INSET,
  },
  [CresultcardLayout.Inline]: {
    count: SINGLE_PLACEHOLDER,
    height: RAIL_CARD_HEIGHT,
    flow: SkeletonFlow.Row,
    width: FULL_WIDTH,
    inset: NO_INSET,
  },
  [CresultcardLayout.Detail]: {
    count: SINGLE_PLACEHOLDER,
    height: DETAIL_HEIGHT,
    flow: SkeletonFlow.Column,
    width: FULL_WIDTH,
    inset: CRC_RAIL_INSET,
  },
};

interface CresultcardLoadingFaceProps {
  readonly layout: CresultcardLayout;
}

export function CresultcardLoadingFace({
  layout,
}: CresultcardLoadingFaceProps): ReactElement {
  const geometry = GEOMETRY_BY_LAYOUT[layout];
  const isDeck = geometry.flow === SkeletonFlow.Row;
  const placeholderIndexes = Array.from(
    { length: geometry.count },
    (_unused, index) => index,
  );

  return (
    <div
      aria-busy
      aria-label={LOADING_LABEL}
      data-el="Cresultcard/LoadingFace/Status"
      data-crc-face="loading"
      role="status"
      style={{
        display: "flex",
        flexDirection: geometry.flow,
        gap: isDeck ? CRC_RAIL_GAP : STACK_GAP,
        padding: geometry.inset,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {placeholderIndexes.map((index) => (
        <div
          key={index}
          style={{
            flex: isDeck ? "0 0 auto" : undefined,
            width: geometry.width,
            minWidth: 0,
          }}
        >
          <Ccontainerskeleton
            height={geometry.height}
            radius={CRC_R_CORE}
          />
        </div>
      ))}
    </div>
  );
}
